import {
  faArrowDownToBracket,
  faCheck,
  faChevronDown,
  faFileImport,
  faUpload
} from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Box,
  Button,
  Container,
  FormControlLabel,
  Grid,
  Menu,
  MenuItem,
  Switch,
  Typography
} from "@mui/material"
import { hasRole } from "auth/hasRole"
import { useApi } from "auth/useApi3"
import { AppContext } from "components/AppContextProvider"
import ClpTable from "components/ClpTable"
import { ClpTableEnum } from "components/ClpTable/ClpTableEnum"
import { MultiSelectFilter, SelectColumnFilter } from "components/ClpTable/ClpTableFilterTypes"
import TableButton from "components/ClpTable/components/actions/TableButton/TableButton"
import { StyledTableButtonGroup } from "components/ClpTable/components/actions/TableButton/tableButtonGroup.styled"
import { TableButtonTypeEnum } from "components/ClpTable/components/actions/TableButton/tableButtonProps.interface"
import TableLink from "components/ClpTable/components/actions/TableLink/TableLink"
import { StyledBoldTypography } from "components/ClpTable/components/typography/typography.styled"
import { GlobalLoaderContext } from "components/GlobalLoadingProvider"
import TooltipInfoIcon from "components/Status/TooltipInfo/TooltipInfoIcon"
import theme from "css/theme/light"
import { apiEndpoints } from "generated/apiEndpoints"
import { allRoles } from "generated/roles"
import { allRoutes } from "generated/routes"
import moment from "moment"
import { useContext, useEffect, useState } from "react"
import { NavLink, useHistory } from "react-router-dom"
import { StyledTableContainer } from "shared/styled/Global"
import { DashboardCard } from "../dashboard/DashboardCard/DashboardCard"
import { VehicleForm } from "./components/VehicleList/VehicleForm"
import { useTranslation } from "react-i18next"
import { StyledFontAwesomeIconForButton } from "shared/styled/General"
import DropdownMenu from "components/DropdownMenu/DropdownMenu"

function ImportMenu({ t }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <>
      <Button variant="outlined" color="secondary" onClick={handleClick}>
        <StyledFontAwesomeIconForButton icon={faArrowDownToBracket} />
        {t("global.import_vehicles", "Import vehicles")}
        <StyledFontAwesomeIconForButton icon={faChevronDown} />
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem component={NavLink} to={allRoutes.VehicleregistryVehiclesImportvtr()} onClick={handleClose}>
          {t("global.import_from_the_transport_agency", "Import from the Transport Agency")}
        </MenuItem>
        <MenuItem component={NavLink} to={allRoutes.VehicleregistryVehiclesImport()} onClick={handleClose}>
          {t("global.import_vehicles", "Import vehicles")}
        </MenuItem>
      </Menu>
    </>
  )
}

const ListVehicles = () => {
  const { t } = useTranslation()
  const [dashboardResult, dashboardFuncs] = useApi([])
  const appContext = useContext(AppContext)
  const loadingContext = useContext(GlobalLoaderContext)
  const [vehiclesResult, vehiclesApi] = useApi()
  const [addVehicleResult, addVehicleApi] = useApi()
  const [editMode, setEditMode] = useState(false)
  const [addOrUpdateResultState, setAddOrUpdateResultState] = useState(null)
  const history = useHistory()
  const [updateVehicleResult, updateVehicleApi] = useApi()
  const [vehicleResult, vehicleApi] = useApi()
  const isVehicleRegistryAdmin = hasRole(appContext.appUser, allRoles.VehicleRegistryAdmin)
  const [issuereportResult, issuereportFuncs] = useApi()
  const [archived, setArchived] = useState(false)
  const vehicleTotalTitle = `${t("vehicleregistry.posted_vehicles", "Posted vehicles")} ${archived ? `(${t("global.inactive2", "Inactive")})` : ""}`
  const [mainPropellantResult, mainPropellantApi] = useApi()

  useEffect(() => {
    vehiclesApi.get(apiEndpoints.vehicleregistry.getvehiclelist + `?archived=${archived}`)
  }, [archived])

  // get dashboard data

  useEffect(() => {
    loadingContext.setLoading(() => vehiclesResult.status === 1)
  })

  useEffect(() => {
    dashboardFuncs.get(apiEndpoints.vehicleregistry.getvtrfordashboard)
    return () => {
      dashboardFuncs.leave()
    }
  }, [dashboardFuncs])

  const getFormData = (id) => {
    setAddOrUpdateResultState(null)
    if (id) {
      setEditMode(true)
      vehicleApi.get(apiEndpoints.vehicleregistryadmin.getvehiclebyid.replace("{id}", id))
    } else {
      setEditMode(false)
    }
    appContext.toggleSideBar("VehicleList", true)
  }

  const onSubmit = (values) => {
    if (editMode) {
      updateVehicleApi.put(apiEndpoints.vehicleregistryadmin.updatevehicle, values)
    } else {
      addVehicleApi.post(apiEndpoints.vehicleregistryadmin.addvehicle, values)
    }
  }

  useEffect(() => {
    if (updateVehicleResult.status === 2 && vehiclesResult.status === 2) {
      const index = vehiclesResult.data.findIndex((item) => item.id === updateVehicleResult.data.id)
      vehiclesResult.data[index] = updateVehicleResult.data
    }
    setAddOrUpdateResultState(updateVehicleResult)
  }, [updateVehicleResult, vehiclesResult.data])

  useEffect(() => {
    if (addVehicleResult.status === 2 && vehiclesResult.data) {
      vehiclesResult.data.push(addVehicleResult.data)
    } else if (addVehicleResult.status === 2 && vehiclesResult.data === null) {
      vehiclesApi.get(apiEndpoints.vehicleregistry.getvehiclelist)
    }
    setAddOrUpdateResultState(addVehicleResult)
  }, [addVehicleResult, vehiclesApi, vehiclesResult.data])

  useEffect(() => {
    if (addVehicleResult.status !== 2) return
    appContext.toggleSideBar("VehicleList", false)
    vehiclesApi.get(apiEndpoints.vehicleregistry.getvehiclelist)
  }, [addVehicleResult])

  useEffect(() => {
    if (updateVehicleResult.status !== 2) return
    appContext.toggleSideBar("VehicleList", false)
    vehiclesApi.get(apiEndpoints.vehicleregistry.getvehiclelist)
  }, [updateVehicleResult])

  useEffect(() => {
    issuereportFuncs.get(apiEndpoints.issuereports.getissuereportsoverview)
    return () => {
      issuereportFuncs.leave()
    }
  }, [issuereportFuncs])

  const columns = [
    {
      header: "",
      accessorKey: "körförbud",
      cell: (cell) => {
        return cell.row.original.körförbud ? <TooltipInfoIcon type="Error" /> : <></>
      },
      meta: {
        mainFilter: true,
        filter: SelectColumnFilter
      }
    },
    {
      accessorKey: "regNumber",
      header: t("global.table.regnr", "Reg No"),
      cell: ({ cell }) => (
        <TableLink
          label={cell.row.original.regNumber}
          to={allRoutes.VehicleregistryVehiclesDetails(cell.row.original.id)}
        />
      )
    },
    {
      accessorKey: "internnumber",
      header: t("global.table.internal_number", "Internal No"),
      cell: (cell) => {
        return <StyledBoldTypography>{cell.row.original.internnumber}</StyledBoldTypography>
      }
    },
    {
      accessorKey: "vehicleType",
      header: t("global.vehicle_type", "Vehicle type")
    },
    {
      accessorKey: "fordonsbenamning",
      header: t("global.fabric", "Fabric"),
      cell: (cell) => {
        return (
          cell.row.original?.fordonsbenamning !== null && cell.row.original?.fordonsbenamning?.slice(0, 15)
        )
      }
    },
    {
      accessorKey: "fordonsAr",
      accessorFn: (row) => (row.fordonsAr ? String(row.fordonsAr) : ""),
      header: t("global.year_model", "Year model"),
      cell: (cell) => {
        return cell.row.original?.fordonsAr !== 0 ? cell.row.original?.fordonsAr : ""
      }
    },
    {
      accessorKey: "company",
      header: t("global.company", "Company"),
      meta: {
        filter: SelectColumnFilter
      }
    },
    {
      accessorKey: "department",
      header: t("global.table.section", "Section"),
      filterFn: "arrIncludesSome",
      meta: {
        mainFilter: true,
        filter: MultiSelectFilter
      }
    },
    {
      accessorKey: "besiktningsDatum",
      accessorFn: (row) => (row.besiktningsDatum ? moment(row.besiktningsDatum).format("YYYY-MM-DD") : ""),
      header: t("global.inspection_last", "Inspection last"),
      cell: (cell) => {
        return cell.row.original?.besiktningsDatum !== null
          ? moment(cell.row.original?.besiktningsDatum).format("YYYY-MM-DD")
          : ""
      }
    },
    {
      accessorKey: "activeIssueReportsTotal",
      header: t("global.active_issue_reports", "Active issue reports"),
      cell: (cell) => {
        const activeIssueReport =
          issuereportResult.status === 2 &&
          issuereportResult?.data?.filter((issue) => issue.vehicleId === cell.row.original.id)?.length
        return <>{activeIssueReport || ""}</>
      }
    },
    {
      accessorFn: (row) => {
        return row.tachographIntegrationActive ? "yes" : "no"
      },
      header: t("global.tachograph", "Tachograph"),
      filterFn: "arrIncludesSome",
      meta: {
        mainFilter: true,
        filter: MultiSelectFilter,
        filterOptions: [
          { name: t("global.yes", "Yes"), value: "yes" },
          { name: t("global.no", "No"), value: "no" }
        ]
      },
      cell: (cell) => {
        return (
          cell.row.original.tachographIntegrationActive && (
            <FontAwesomeIcon icon={faCheck} color={theme.palette.grey[600]} />
          )
        )
      }
    }
  ]

  if (hasRole(appContext.appUser, allRoles.VehicleRegistryAdmin)) {
    columns.push({
      cell: (cell) => {
        return (
          <StyledTableButtonGroup>
            <TableButton
              type={TableButtonTypeEnum.Edit}
              handleOnClick={() => {
                getFormData(cell.row.original.id)
              }}
            />
          </StyledTableButtonGroup>
        )
      },
      accessorKey: "action",
      header: "",
      meta: {
        hiddenFilter: true,
        align: "right"
      }
    })
  }

  return (
    <>
      <Container>
        <VehicleForm
          vehicleResult={vehicleResult}
          mainPropellantResult={mainPropellantResult}
          mainPropellantApi={mainPropellantApi}
          onSubmit={onSubmit}
          editMode={editMode}
          addOrUpdateResultState={addOrUpdateResultState}
        />
        <Box mb={5}>
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ gap: { xs: "20px" } }}>
            <Typography variant="h1">{t("features.title.vehicleregistry", "Vehicle registry")}</Typography>

            {hasRole(appContext.appUser, allRoles.VehicleRegistryAdmin) && (
              <Grid item sx={{ display: "flex", flexWrap: "wrap", gap: { xs: "20px" } }}>
                <DropdownMenu
                  t={t}
                  buttonLabel={t("global.import_vehicles", "Import vehicles")}
                  startIcon={faArrowDownToBracket}
                  endIcon={faChevronDown}
                  items={[
                    {
                      label: t("global.import_from_the_transport_agency", "Import from the Transport Agency"),
                      to: allRoutes.VehicleregistryVehiclesImportvtr()
                    },
                    {
                      label: t("global.import_vehicles", "Import vehicles"),
                      to: allRoutes.VehicleregistryVehiclesImport()
                    }
                  ]}
                />

                <Button variant="contained" color="primary" component={Button} onClick={() => getFormData()}>
                  {t("global.add_vehicle", "Add vehicle")}
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>
        <Box>
          <Grid spacing={2} container direction="row" justifyContent="spaceBetween">
            <Grid item md={8} lg={12} direction="row">
              <Grid container direction="row" spacing={2}>
                <DashboardCard
                  icon={faUpload}
                  route={allRoutes.VehicleregistryVehiclesImportvtr}
                  itemLength={vehiclesResult.data?.length}
                  title={vehicleTotalTitle}
                  quantityType={t("global.pc", "pcs")}
                  status={vehiclesResult.status}
                  xlGridSize={3}
                />
                {isVehicleRegistryAdmin && (
                  <DashboardCard
                    icon={faFileImport}
                    route={allRoutes.VehicleregistryVehiclesImportvtr()}
                    itemLength={dashboardResult.data}
                    title={t("global.import_from_the_transport_agency", "Import from the Transport Agency")}
                    quantityType={t("global.pc", "pcs")}
                    status={dashboardResult.status}
                    xlGridSize={3}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Container>
      <StyledTableContainer>
        <Box pt={{ xs: 2, sm: 3 }} pl={{ xs: 2, sm: 3 }}>
          <FormControlLabel
            sx={{ marginRight: "4px" }}
            control={
              <Switch
                onChange={() => {
                  setArchived(!archived)
                }}
                checked={archived}
              />
            }
            label={t("vehicleregistry.view_archived", "View archived")}
          />
        </Box>
        <ClpTable
          columns={columns}
          onRowClick={(row) => history.push(allRoutes.VehicleregistryVehiclesDetails(row.original.id))}
          rows={vehiclesResult.status === 2 && vehiclesResult.data}
          loadingState={vehiclesResult.status}
          tableId={ClpTableEnum.VehicleRegistryVehicles}
          saveFilterTooltipText={t(
            "vehicleregistry.saved_filters_control",
            "Saved filters control which vehicles you can browse between inside the vehicle."
          )}
        />
      </StyledTableContainer>
    </>
  )
}

export default ListVehicles
